import { useState } from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { PublicShell } from "@/components/PublicShell"
import { Button } from "@/components/ui/button"
import { RichTextField } from "@/components/ui/rich-text-field"

type RecordLabelFormProps = {
  id: number | null
  name: string
  slug?: string
  content: string | null
}

type Props = {
  record_label: RecordLabelFormProps
  return_to?: string | null
  errors?: Record<string, string>
}

const ic = "w-full rounded border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-1 focus:ring-amber-500"

export default function RecordLabelForm({ record_label, return_to }: Props) {
  const { errors = {} } = usePage<Props>().props
  const isNew = record_label.id === null

  const [form, setForm] = useState({
    name:    record_label.name ?? "",
    content: record_label.content ?? "",
  })

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const payload = return_to ? { record_label: form, return_to } : { record_label: form }
    if (isNew) router.post("/record_labels", payload)
    else router.patch(`/record_labels/${record_label.slug}`, payload)
  }

  const pageTitle  = isNew ? "New record label" : `Edit ${record_label.name}`
  const cancelHref = return_to ?? (record_label.slug ? `/record_labels/${record_label.slug}` : "/record_labels")

  return (
    <>
      <Head title={pageTitle}>
        <meta name="description" content={pageTitle} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageTitle} />
      </Head>
      <PublicShell>
        <div className="max-w-xl mx-auto px-4 sm:px-6 py-8">
          <h1 className="mb-6">{pageTitle}</h1>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm text-stone-500 dark:text-stone-400 mb-1">Name *</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                autoFocus
                className={ic}
                placeholder="e.g. Festival Records"
              />
              {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm text-stone-500 dark:text-stone-400 mb-1">Description</label>
              <RichTextField
                defaultValue={form.content}
                onChange={(val) => setForm((prev) => ({ ...prev, content: val }))}
                placeholder="Optional label history or notes…"
              />
              {errors.content && <p className="mt-1 text-xs text-red-600">{errors.content}</p>}
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit">{isNew ? "Create label" : "Save changes"}</Button>
              <Link href={cancelHref} className="text-sm text-stone-500 hover:text-stone-700 dark:hover:text-stone-300 no-underline">
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </PublicShell>
    </>
  )
}
